import { defineStore } from 'pinia';
import { type Language } from '../domain/language';
import { english } from '../languages/en';
import { swedish } from '../languages/sv';
import { german } from '../languages/de';
import { computed, ref } from 'vue';

export const useLanguageStore = defineStore('language', () => {
  const localStorageKey = 'prefered-language';

  const englishLanguage: Language = {
    code: 'en-GB',
    title: 'English',
    translations: english,
  };

  const swedishLanguage: Language = {
    code: 'sv-SE',
    title: 'Svenska',
    translations: swedish,
  };

  const germanLanguage: Language = {
    code: 'de-DE',
    title: 'Deutsch',
    translations: german,
  };

  const supportedLanguages: Language[] = [englishLanguage, swedishLanguage, germanLanguage];

  const language = ref<Language>(englishLanguage);

  const translations = computed(() => language.value.translations);

  const dateFormat = computed(() => {
    return new Intl.DateTimeFormat(language.value.code, {
      timeStyle: 'medium',
      dateStyle: 'short',
    });
  });

  const monthDayFormat = computed(() => {
    return new Intl.DateTimeFormat(language.value.code, {
      year: undefined,
      month: 'numeric',
      day: 'numeric',
      hour: undefined,
      minute: undefined,
      second: undefined,
    });
  });

  const dateTimeFormat = computed(() => {
    return new Intl.DateTimeFormat(language.value.code, {
      year: undefined,
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: undefined,
    });
  });

  const preferedCode = localStorage.getItem(localStorageKey);

  if (preferedCode && preferedCode !== '') {
    select(preferedCode);
  }

  function select(code: string) {
    for (const lang of supportedLanguages) {
      if (lang.code === code) {
        language.value = lang;
        localStorage.setItem(localStorageKey, code);
        return;
      }
    }

    if (supportedLanguages && supportedLanguages[0]) {
      language.value = supportedLanguages[0];
    }
  }

  function formatDate(value: number | string | null) {
    if (value === null) return '';
    if (typeof value === 'string') {
      return dateFormat.value.format(Date.parse(value));
    } else {
      return dateFormat.value.format(value);
    }
  }

  function formatMonthDay(value: string | null) {
    if (value === null) return '';
    const date = Date.parse(value);
    return monthDayFormat.value.format(date);
  }

  function formatDateTime(value: string | null) {
    if (value === null) return '';
    const date = Date.parse(value);
    return dateTimeFormat.value.format(date);
  }

  function formatDateRange(startTimestamp: number, endTimestamp: number) {
    const duration = endTimestamp - startTimestamp;
    if (duration > 1000) {
      return `${duration / 1000}s`;
    }
    return `${duration}ms`;
  }

  // Get PrimeVue-compatible date format string based on current locale
  function getPrimeVueDateFormat(): string {
    if (language.value.code.startsWith('en-US')) {
      return 'mm/dd/yy';
    } else if (language.value.code.startsWith('en')) {
      return 'dd/mm/yy';
    } else if (language.value.code.startsWith('sv')) {
      return 'yy-mm-dd';
    } else if (language.value.code.startsWith('de')) {
      return 'dd.mm.yy';
    }
    return 'dd/mm/yy';
  }

  function getPrimeVueLocale(): string {
    return language.value.code.split('-')[0] ?? 'en';
  }

  function formatNumber(value: number, fractionDigits: number = 0): string {
    return new Intl.NumberFormat(language.value.code, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
      useGrouping: true,
    }).format(value);
  }

  return {
    language,
    supportedLanguages,
    translations,
    select,
    formatDate,
    formatDateRange,
    formatMonthDay,
    formatDateTime,
    getPrimeVueDateFormat,
    getPrimeVueLocale,
    formatNumber,
  };
});
